import type { GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import type { TelemetryDiagnostics } from '../../domain/telemetry/processed/TelemetryDiagnostics'
import type { Lap } from '../../domain/lap/Lap'
import type { Finding } from '../finding'
import { LAP_CHECKS } from '../checks'

export type LapUnreliableReason = 'gpsJump' | 'timeUnreliable'

export const LAP_UNRELIABLE_REASON_TEXT: Record<LapUnreliableReason, string> = {
  gpsJump: 'GPS jumped: distance unreliable',
  timeUnreliable: 'Lap time unreliable: the recording’s clock stalls',
}

const LAP_UNRELIABLE_REASONS = Object.keys(
  LAP_UNRELIABLE_REASON_TEXT
) as LapUnreliableReason[]

interface WithUnreliableReasons {
  readonly unreliableReasons?: readonly LapUnreliableReason[]
}

export const unreliableLapText = ({
  unreliableReasons = [],
}: WithUnreliableReasons): string =>
  unreliableReasons
    .map((reason) => LAP_UNRELIABLE_REASON_TEXT[reason])
    .join('; ')

export const isLapUnreliableReason = (
  value: unknown
): value is LapUnreliableReason =>
  LAP_UNRELIABLE_REASONS.includes(value as LapUnreliableReason)

export interface LapCheckInput {
  gpsPoints: readonly GpsPoint[]
  diagnostics: TelemetryDiagnostics | undefined
}

export interface LapCheck {
  id: string
  reason: LapUnreliableReason
  isUnreliable(input: LapCheckInput): boolean
}

export function lapUnreliableReasons(
  input: LapCheckInput
): LapUnreliableReason[] {
  return LAP_CHECKS.filter((check) => check.isUnreliable(input)).map(
    (check) => check.reason
  )
}

export const isUnreliableLap = ({
  unreliableReasons,
}: WithUnreliableReasons): boolean => (unreliableReasons?.length ?? 0) > 0

export const reliableOnly = (laps: Lap[]): Lap[] =>
  laps.every((lap) => lap.isReliable)
    ? laps
    : laps.filter((lap) => lap.isReliable)

export function lapFindingsOf(laps: readonly Lap[]): Finding[] {
  return LAP_CHECKS.flatMap((check): Finding[] => {
    const lapNumbers = laps
      .filter((lap) => lap.unreliableReasons.includes(check.reason))
      .map((lap) => lap.lapNumber)
    if (lapNumbers.length === 0) return []
    return [
      {
        check: check.id,
        level: 'lap',
        verdict: 'unreliable',
        laps: lapNumbers,
        reason: LAP_UNRELIABLE_REASON_TEXT[check.reason],
      },
    ]
  })
}
